/**
 * Agent model + effort options.
 *
 * The lists are served by papaya (`GET /openai/chat/agent/models`, built in
 * `routes/openai/agentModelCatalog.ts`) so adding or retiring a model reaches
 * every client without a release. **Do not add a model here** — add it to
 * papaya's `AGENT_MODELS` and `MODEL_DISPLAY`.
 *
 * What stays in this file:
 *  - types and pure helpers
 *  - FALLBACK_AGENT_MODEL_CATALOG, which is `auto` and nothing else. `auto` is
 *    the routing mode rather than a model, so it is valid against any papaya.
 *    No models, no effort scale, no image models: anything bundled here is a
 *    claim this build cannot verify, and goes stale the moment the lineup moves.
 *    The effort control stays hidden until a catalog defines the scale.
 */

/** Capability band, derived server-side. Lets copy describe a model without
 *  naming it, so it survives the lineup changing. */
export type AgentModelTier = 'fast' | 'standard' | 'high' | 'frontier'

export type AgentModelOption = {
  key: string
  label: string
  description: string
  /** Free-form badges from the server (`recommended`, `fastest`, `premium`, …). */
  tags: string[]
  provider: string
  /** Cost relative to the 1x tier; null for `auto`, whose tier varies per turn. */
  creditMultiplier: number | null
  /** null for `auto`, which spans every band depending on the question. */
  tier: AgentModelTier | null
}

/** Which model key currently fills each role. Derived server-side from the Auto
 *  ladder and the Deep Dive pins, so no client hardcodes a model name. */
export type AgentModelRoles = {
  fast: string
  standard: string
  high: string
  frontier: string
  deepDiveLead: string
  deepDiveExecution: string
}

export type EffortLevelOption = {
  level: number
  label: string
  description: string
}

export type ImageModelOption = {
  key: string
  label: string
}

export type AgentModelCatalog = {
  schemaVersion: number
  defaultModel: string
  models: AgentModelOption[]
  /** null until a catalog arrives — there is no local notion of the scale. */
  defaultEffortLevel: number | null
  minEffortLevel: number | null
  maxEffortLevel: number | null
  /** Ordinary effort stops. Deep Dive is separate — it renders as its own toggle. */
  effortLevels: EffortLevelOption[]
  deepDive: (EffortLevelOption & { leadModel?: string; executionModel?: string }) | null
  /** Absent from a papaya older than the roles payload. */
  modelRoles?: AgentModelRoles
  defaultImageModel: string
  imageModels: ImageModelOption[]
}

export const AGENT_MODEL_CATALOG_SCHEMA_VERSION = 1

export const DEFAULT_AGENT_MODEL = 'auto'



export const FALLBACK_AGENT_MODEL_CATALOG: AgentModelCatalog = {
  schemaVersion: AGENT_MODEL_CATALOG_SCHEMA_VERSION,
  defaultModel: DEFAULT_AGENT_MODEL,
  models: [
    { key: 'auto', label: 'Auto', description: 'Picks a model from the question and your effort level.', tags: ['recommended'], provider: 'auto', creditMultiplier: null, tier: null },
  ],
  defaultEffortLevel: null,
  minEffortLevel: null,
  maxEffortLevel: null,
  effortLevels: [],
  deepDive: null,
  defaultImageModel: '',
  imageModels: [],
}

const asString = (value: unknown, fallback: string): string =>
  typeof value === 'string' && value.trim() ? value.trim() : fallback

const asModelOption = (raw: any): AgentModelOption | null => {
  const key = typeof raw?.key === 'string' ? raw.key.trim() : ''
  if (!key) return null
  return {
    key,
    label: asString(raw?.label, key),
    description: asString(raw?.description, ''),
    tags: Array.isArray(raw?.tags) ? raw.tags.filter((tag: unknown) => typeof tag === 'string') : [],
    provider: asString(raw?.provider, 'unknown'),
    creditMultiplier: typeof raw?.creditMultiplier === 'number' ? raw.creditMultiplier : null,
    tier: TIERS.includes(raw?.tier) ? raw.tier : null,
  }
}

const TIERS: readonly unknown[] = ['fast', 'standard', 'high', 'frontier']

const ROLE_KEYS = ['fast', 'standard', 'high', 'frontier', 'deepDiveLead', 'deepDiveExecution'] as const

/** Roles are optional: a papaya older than this payload simply omits them, and
 *  callers fall back to copy that names no model. Partial is treated as absent —
 *  a half-filled role map would render "undefined directs and reviews". */
const asModelRoles = (raw: any): AgentModelRoles | undefined => {
  if (!raw || typeof raw !== 'object') return undefined
  const roles: Record<string, string> = {}
  for (const key of ROLE_KEYS) {
    const value = typeof raw[key] === 'string' ? raw[key].trim() : ''
    if (!value) return undefined
    roles[key] = value
  }
  return roles as AgentModelRoles
}

const asEffortOption = (raw: any): EffortLevelOption | null => {
  const level = Number(raw?.level)
  if (!Number.isInteger(level)) return null
  return {
    level,
    label: asString(raw?.label, `Level ${level}`),
    description: asString(raw?.description, ''),
  }
}

/**
 * Shape a catalog response, falling back whole rather than piecemeal: a payload
 * missing models or effort levels would render an empty picker, which is worse
 * than a slightly stale one. A newer schemaVersion is accepted as long as the
 * fields we read are present, since v1 fields are additive-only by convention.
 */
export const normalizeAgentModelCatalog = (raw: unknown): AgentModelCatalog => {
  const payload = raw as any
  if (!payload || typeof payload !== 'object') return FALLBACK_AGENT_MODEL_CATALOG

  const models = Array.isArray(payload.models)
    ? payload.models.map(asModelOption).filter(Boolean) as AgentModelOption[]
    : []
  const effortLevels = Array.isArray(payload.effortLevels)
    ? payload.effortLevels.map(asEffortOption).filter(Boolean) as EffortLevelOption[]
    : []
  // Only a payload with no usable models is malformed. Missing effort data just
  // leaves the control hidden — there is no bundled scale to substitute.
  if (models.length === 0) return FALLBACK_AGENT_MODEL_CATALOG
  const deepDive = asEffortOption(payload.deepDive)

  const imageModels = Array.isArray(payload.imageModels)
    ? payload.imageModels
      .map((entry: any) => {
        const key = typeof entry?.key === 'string' ? entry.key.trim() : ''
        return key ? { key, label: asString(entry?.label, key) } : null
      })
      .filter(Boolean) as ImageModelOption[]
    : []


  return {
    schemaVersion: Number.isInteger(payload.schemaVersion) ? payload.schemaVersion : AGENT_MODEL_CATALOG_SCHEMA_VERSION,
    defaultModel: asString(payload.defaultModel, DEFAULT_AGENT_MODEL),
    models,
    defaultEffortLevel: Number.isInteger(payload.defaultEffortLevel) ? payload.defaultEffortLevel : null,
    minEffortLevel: Number.isInteger(payload.minEffortLevel) ? payload.minEffortLevel : null,
    maxEffortLevel: Number.isInteger(payload.maxEffortLevel) ? payload.maxEffortLevel : null,
    effortLevels,
    deepDive: deepDive == null ? null : {
      ...deepDive,
      ...(typeof payload.deepDive?.leadModel === 'string' ? { leadModel: payload.deepDive.leadModel } : {}),
      ...(typeof payload.deepDive?.executionModel === 'string' ? { executionModel: payload.deepDive.executionModel } : {}),
    },
    modelRoles: asModelRoles(payload.modelRoles),
    defaultImageModel: asString(payload.defaultImageModel, ''),
    imageModels,
  }
}

export const isModelInCatalog = (catalog: AgentModelCatalog, model: unknown): boolean =>
  typeof model === 'string' && catalog.models.some(({ key }) => key === model)

export const isImageModelInCatalog = (catalog: AgentModelCatalog, model: unknown): boolean =>
  typeof model === 'string' && catalog.imageModels.some(({ key }) => key === model)

/** True once the server has told us what the effort scale is. The UI hides the
 *  effort control until then rather than inventing one. */
export const hasEffortScale = (catalog: AgentModelCatalog): boolean =>
  catalog.effortLevels.length > 0

export const isDeepDiveLevel = (catalog: AgentModelCatalog, level: number | null): boolean =>
  catalog.deepDive != null && level != null && level === catalog.deepDive.level

const selectableLevels = (catalog: AgentModelCatalog): number[] => [
  ...catalog.effortLevels.map(({ level }) => level),
  ...(catalog.deepDive ? [catalog.deepDive.level] : []),
]

/**
 * Settle a stored effort level against the levels the server offers. Returns
 * null while the scale is unknown so callers can omit the field and let papaya
 * apply its own default rather than guessing a number locally.
 */
export const resolveEffortLevel = (
  catalog: AgentModelCatalog,
  stored: number | null,
): number | null => {
  if (!hasEffortScale(catalog)) return null
  const offered = selectableLevels(catalog)
  if (stored != null && offered.includes(stored)) return stored
  if (catalog.defaultEffortLevel != null && offered.includes(catalog.defaultEffortLevel)) {
    return catalog.defaultEffortLevel
  }
  return catalog.effortLevels[0]?.level ?? null
}
